import { addHours } from 'date-fns';
import { RecordContextProvider, TextField, useGetList, useRecordContext } from 'react-admin';

import { B2B_CAMPAIGN_GRACE_PERIOD_HOURS } from '@Common/constants/b2b/campaigns';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { B2bCompanyDocumentDetails } from '@Common/types/bo/b2b/companies';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { b2bCampaignPaymentMethodChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { ResourceBasePath } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type AdditionalSfr = NonNullable<
  GetB2bCampaignOneResponse['additional_savings_funds_requests']
>[number];

const CampaignOperationSummaryDocument = ({
  resourceBasePath,
  campaignId,
  companyId,
}: {
  resourceBasePath: ResourceBasePath;
  campaignId: number;
  companyId: number;
}) => {
  const { data } = useGetList<B2bCompanyDocumentDetails>(`${resourceBasePath}companies/documents`, {
    filter: {
      b2b_campaign_id: campaignId,
      company_id: companyId,
      type: 'campaign_operation_summary',
      is_archived: false,
    },
    sort: { field: 'created_at', order: 'DESC' },
    pagination: { page: 1, perPage: 1 },
  });

  return data && data.length > 0 ? (
    <DocumentListViewerLink record={data[0]} urlSource="document_url" nameSource="document_name" />
  ) : (
    <TextField data-testid="BoExternalCampaignOperationSummaryDocument_EmptyField" />
  );
};

const SfrFundRequestDocument = ({
  documents,
  showDocument,
  savingsFundRequestId,
}: {
  documents: B2bCompanyDocumentDetails[];
  showDocument: boolean;
  savingsFundRequestId: number | null;
}) => {
  if (!showDocument) {
    return null;
  }

  const doc = documents.find((doc) => doc.savings_funds_request_id === savingsFundRequestId);
  return doc ? (
    <DocumentListViewerLink record={doc} urlSource="document_url" nameSource="document_name" />
  ) : (
    <TextField data-testid="BoExternalCampaignFundRequestDocument_EmptyField" />
  );
};

const BankAccountInfo = ({
  resourceBasePath,
  companyId,
}: {
  resourceBasePath: ResourceBasePath;
  companyId: number;
}) => {
  const { data: paymentMethods } = useGetList<{
    id: number;
    iban: string;
    intent: string;
    type: string;
  }>(`${resourceBasePath}companies/payment_methods`, {
    filter: {
      b2b_company_id: companyId,
      intent: 'principal',
      type: 'sepa_direct_debit',
    },
    pagination: { page: 1, perPage: 1 },
  });

  const iban = paymentMethods?.[0]?.iban;

  if (!iban) {
    return <TextField emptyText="-" data-testid="BoExternalBankAccountInfo_EmptyIbanField" />;
  }

  return (
    <TextField source="iban" record={{ iban }} data-testid="BoExternalBankAccountInfo_IbanField" />
  );
};

export const B2bCampaignPaymentSection = ({
  resourceBasePath,
}: {
  resourceBasePath: ResourceBasePath;
}) => {
  const campaign = useRecordContext<GetB2bCampaignOneResponse>();

  const { data: fundRequestDocuments = [] } = useGetList<B2bCompanyDocumentDetails>(
    `${resourceBasePath}companies/documents`,
    {
      filter: {
        b2b_campaign_id: campaign?.id,
        company_id: campaign?.company_id,
        type: 'campaign_fund_request',
        is_archived: false,
      },
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 25 },
    },
    { enabled: !!campaign },
  );

  if (!campaign) {
    return null;
  }

  const directDebitDate = campaign.end_date
    ? addHours(new Date(campaign.end_date), B2B_CAMPAIGN_GRACE_PERIOD_HOURS)
    : null;

  const isAfterConsultationPeriod = campaign.end_date
    ? new Date() > new Date(campaign.end_date)
    : false;

  const sharedPaymentFields: Record<string, React.JSX.Element | undefined> = {
    payment_method: (
      <SelectChipField
        choices={b2bCampaignPaymentMethodChoices}
        data-testid="BoExternalB2bCampaignPaymentSection_PaymentMethodField"
      />
    ),
  };

  if (campaign.payment_method === 'sepa') {
    sharedPaymentFields.bank_account = (
      <BankAccountInfo resourceBasePath={resourceBasePath} companyId={campaign.company_id} />
    );
    sharedPaymentFields.direct_debit_date = directDebitDate ? (
      <UTCDateField
        source="direct_debit_date"
        record={{ direct_debit_date: directDebitDate }}
        data-testid="BoExternalB2bCampaignPaymentSection_DirectDebitDateField"
      />
    ) : undefined;
  }

  return (
    <>
      <ShowSection label="resources.bo_common.b2b.campaigns.sections.sfr.title">
        <ShowFields<
          GetB2bCampaignOneResponse & {
            fund_request_document: string;
            campaign_operation_summary: string;
          }
        >
          resource="bo_common.b2b.campaigns"
          labels={{
            savings_funds_request_total_net_amount:
              'resources.bo_common.b2b.campaigns.fields.total_net_amount',
          }}
          fields={{
            ...(!isBoExternal() && {
              savings_funds_request_id: (
                <IdField
                  // eslint-disable-next-line regex/invalid -- only for internal BO
                  reference="b2b/savings_funds_requests"
                  data-testid="BoExternalB2bCampaignPaymentSection_SavingsFundRequestIdField"
                />
              ),
            }),
            savings_funds_request_total_net_amount: (
              <CurrencyField
                currency="EUR"
                testId="BoExternalB2bCampaignPaymentSection_TotalNetAmountField"
              />
            ),
            ...sharedPaymentFields,
            campaign_operation_summary: (
              <CampaignOperationSummaryDocument
                resourceBasePath={resourceBasePath}
                campaignId={campaign.id}
                companyId={campaign.company_id}
              />
            ),
            fund_request_document: (
              <SfrFundRequestDocument
                documents={fundRequestDocuments}
                showDocument={isAfterConsultationPeriod}
                savingsFundRequestId={campaign.savings_funds_request_id}
              />
            ),
          }}
        />
      </ShowSection>

      {(campaign.additional_savings_funds_requests ?? []).map((sfr) => (
        <RecordContextProvider key={sfr.id} value={sfr}>
          <ShowSection label="resources.bo_common.b2b.campaigns.sections.secondary_sfr.title">
            <ShowFields<AdditionalSfr & { fund_request_document: string }>
              resource="bo_common.b2b.campaigns"
              labels={{
                id: 'resources.bo_common.b2b.campaigns.fields.savings_funds_request_id',
                total_net_amount: 'resources.bo_common.b2b.campaigns.fields.total_net_amount',
              }}
              fields={{
                ...(!isBoExternal() && {
                  id: (
                    <IdField
                      reference={`${resourceBasePath}savings_funds_requests`}
                      data-testid="BoExternalB2bCampaignPaymentSection_AdditionalSavingsFundRequestIdField"
                    />
                  ),
                }),
                total_net_amount: (
                  <CurrencyField
                    currency="EUR"
                    testId="BoExternalB2bCampaignPaymentSection_AdditionalTotalNetAmountField"
                  />
                ),
                fund_request_document: (
                  <SfrFundRequestDocument
                    documents={fundRequestDocuments}
                    showDocument={isAfterConsultationPeriod}
                    savingsFundRequestId={sfr.id}
                  />
                ),
              }}
            />
          </ShowSection>
        </RecordContextProvider>
      ))}
    </>
  );
};
